'use client'

import { useState, useEffect } from 'react'
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  MessageSquare,
  HelpCircle,
  CheckCircle,
  Globe,
  Users,
  HeadphonesIcon,
  Loader
} from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'

interface ContactInfo {
  icon: any
  title: string
  details: string[]
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    category: 'general'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contactInfo, setContactInfo] = useState<ContactInfo[]>([])
  const [companyAddress, setCompanyAddress] = useState('')
  const { showToast } = useToast()

  // Fetch contact information from environment variables via API
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch('/api/contact-info')
        if (response.ok) {
          const data = await response.json()
          
          // Set company address for map section
          const addressParts = [
            data.addressLine1,
            data.addressLine2,
            data.addressLine3,
            data.addressLine4
          ].filter(Boolean)
          setCompanyAddress(addressParts.join(', '))
          
          setContactInfo([
            {
              icon: MapPin,
              title: "Our Address",
              details: [
                data.companyName,
                data.addressLine1,
                data.addressLine2,
                data.addressLine3,
                data.addressLine4
              ].filter(Boolean)
            },
            {
              icon: Phone,
              title: "Phone Numbers",
              details: [
                data.phoneCustomer && `Customer Service: ${data.phoneCustomer}`,
                data.phoneBusiness && `Business Inquiries: ${data.phoneBusiness}`,
                data.phoneTechnical && `Technical Support: ${data.phoneTechnical}`,
                data.phoneTollFree && `Toll Free: ${data.phoneTollFree}`
              ].filter(Boolean)
            },
            {
              icon: Mail,
              title: "Email Address",
              details: [
                data.emailGeneral && `General: ${data.emailGeneral}`,
                data.emailSupport && `Support: ${data.emailSupport}`,
                data.emailBusiness && `Business: ${data.emailBusiness}`,
                data.emailCareers && `Careers: ${data.emailCareers}`
              ].filter(Boolean)
            },
            {
              icon: Clock,
              title: "Business Hours",
              details: [
                data.hoursWeekdays,
                data.hoursSaturday,
                data.hoursSunday,
                data.hoursOnline
              ].filter(Boolean)
            }
          ])
        }
      } catch (error) {
        console.error('Error fetching contact info:', error)
        // Fallback to default values if API fails
        setCompanyAddress('123 Business Plaza, Sector 18, Gurgaon, Haryana 122015')
        setContactInfo([
          {
            icon: MapPin,
            title: "Our Address",
            details: [
              "Hariom Market Headquarters",
              "123 Business Plaza, Sector 18",
              "Gurgaon, Haryana 122015",
              "India"
            ]
          },
          {
            icon: Phone,
            title: "Phone Numbers",
            details: [
              "Customer Service: +91-124-4567890",
              "Business Inquiries: +91-124-4567891",
              "Technical Support: +91-124-4567892"
            ]
          },
          {
            icon: Mail,
            title: "Email Address",
            details: [
              "General: info@hariommarket.com",
              "Support: support@hariommarket.com",
              "Business: business@hariommarket.com",
              "Careers: careers@hariommarket.com"
            ]
          },
          {
            icon: Clock,
            title: "Business Hours",
            details: [
              "Monday - Friday: 9:00 AM - 6:00 PM",
              "Saturday: 9:00 AM - 4:00 PM",
              "Sunday: 10:00 AM - 2:00 PM",
              "24/7 Online Support Available"
            ]
          }
        ])
      }
    }

    fetchContactInfo()
  }, [])

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'support', label: 'Customer Support' },
    { value: 'business', label: 'Business Partnership' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'careers', label: 'Career Opportunities' }
  ]

  const faqs = [
    {
      question: "How can I track my order?",
      answer: "You can track your order by logging into your account and visiting the 'My Orders' section. You'll also receive tracking updates via email and SMS."
    },
    {
      question: "What is your return policy?",
      answer: "We offer a 30-day return policy for most items. Products must be in original condition with all tags and packaging. Some restrictions apply to certain categories."
    },
    {
      question: "How long does shipping take?",
      answer: "Standard shipping takes 3-5 business days. Express shipping is available for 1-2 business days. Same-day delivery is available in select cities."
    },
    {
      question: "Do you offer international shipping?",
      answer: "Yes, we ship to over 50 countries worldwide. International shipping times vary by destination and typically take 7-14 business days."
    },
    {
      question: "How can I contact customer support?",
      answer: "You can reach our customer support team via phone, email, live chat, or by filling out the contact form on this page. We're available 24/7 to assist you."
    },
    {
      question: "Are your products authentic?",
      answer: "Yes, all our products are 100% authentic and sourced directly from authorized distributors and manufacturers. We guarantee authenticity on all items."
    }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name || !formData.email || !formData.message) {
      showToast('Please fill in all required fields', 'error')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (response.ok) {
        showToast(
          `${data.message} (Ticket #${data.ticketId} - Expected response: ${data.estimatedResponse})`, 
          'success'
        )
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          category: 'general'
        })
      } else {
        showToast(data.error || 'Something went wrong. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      showToast('Network error. Please check your connection and try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-8">
              We're here to help! Get in touch with our friendly support team
            </p>
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              <div className="text-center">
                <div className="bg-white bg-opacity-20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <HeadphonesIcon className="w-8 h-8" />
                </div>
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-sm opacity-80">Support Available</div>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-8 h-8" />
                </div>
                <div className="text-2xl font-bold">&lt;1hr</div>
                <div className="text-sm opacity-80">Average Response</div>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-8 h-8" />
                </div>
                <div className="text-2xl font-bold">50+</div>
                <div className="text-sm opacity-80">Support Agents</div>
              </div>
              <div className="text-center">
                <div className="bg-white bg-opacity-20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <Globe className="w-8 h-8" />
                </div>
                <div className="text-2xl font-bold">10+</div>
                <div className="text-sm opacity-80">Languages</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  Send us a Message
                </h2>
                <p className="text-gray-600 mb-8">
                  Have a question or need assistance? Fill out the form below and we'll get back to you as soon as possible.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {categories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter the subject of your message"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Tell us how we can help you..."
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  Get in Touch
                </h2>
                <p className="text-gray-600 mb-8">
                  Reach out to us through any of the following channels. We're always happy to help!
                </p>

                <div className="space-y-8">
                  {contactInfo.map((info, index) => {
                    const IconComponent = info.icon
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg p-3 flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            {info.title}
                          </h3>
                          <div className="space-y-1">
                            {info.details.map((detail, detailIndex) => (
                              <p key={detailIndex} className="text-gray-600">
                                {detail}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Quick Links */}
                <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    Quick Links
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <a href="/help" className="text-blue-600 hover:text-blue-800 transition-colors">
                      Help Center
                    </a>
                    <a href="/shipping" className="text-blue-600 hover:text-blue-800 transition-colors">
                      Shipping Info
                    </a>
                    <a href="/returns" className="text-blue-600 hover:text-blue-800 transition-colors">
                      Returns
                    </a>
                    <a href="/faq" className="text-blue-600 hover:text-blue-800 transition-colors">
                      FAQ
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Quick answers to common questions about our products and services.
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-blue-500 to-purple-500 rounded-full p-2 flex-shrink-0">
                        <HelpCircle className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">
                Can't find what you're looking for?
              </p>
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Ask a Question
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Visit Our Office
              </h2>
              <p className="text-xl text-gray-600">
                Drop by our headquarters in Gurgaon for face-to-face support and assistance.
              </p>
            </div>

            <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Interactive Map
                </h3>
                <p className="text-gray-500">
                  Map integration would be implemented here
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {companyAddress}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
} 